/**
 * useAttributeNarrowing (ORCH-389)
 *
 * Purpose: The limits a membership's security-attribute pickers take from each
 *          other. A dimension `/authz/dimensions` marks `narrowed_by` offers only
 *          the values related to what the person already holds on the other
 *          dimension: Media Subtype follows Media Type. Nothing chosen on the
 *          other dimension leaves the picker unlimited.
 * Logic: Reads the dimensions once (cached), turns each narrowed one into a
 *        Narrowing over the draft's values, and hands them to
 *        useNarrowedPickerValues. The result is what PickerValues takes.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { authzService } from '../../services/authzService';
import { Dimension } from '../../types/authz';
import { asList } from '../../utils/listValues';
import { Narrowing, useNarrowedPickerValues } from '../../hooks/useNarrowedPickerValues';

export const DIMENSIONS_QUERY_KEY = ['authz', 'dimensions'] as const;

/** The narrowings the dimensions declare, over a membership's attribute values. */
export const attributeNarrowings = (
  dimensions: Dimension[],
  metadata: Record<string, unknown>
): Narrowing[] =>
  dimensions.flatMap((d) =>
    d.narrowed_by
      ? [
          {
            fielddefId: d.picker_fielddef_id,
            byFielddefId: d.narrowed_by.picker_fielddef_id,
            relatedFielddefId: d.narrowed_by.fielddef_id,
            chosen: asList(metadata[d.narrowed_by.picker_fielddef_id]),
          },
        ]
      : []
  );

export const useAttributeNarrowing = (
  tenantId: string,
  metadata: Record<string, unknown>
): Record<string, string[]> => {
  const { data: dimensions } = useQuery({
    queryKey: DIMENSIONS_QUERY_KEY,
    queryFn: () => authzService.getDimensions(),
    staleTime: 5 * 60 * 1000,
  });
  const narrowings = useMemo(
    () => attributeNarrowings(dimensions ?? [], metadata),
    [dimensions, metadata]
  );
  return useNarrowedPickerValues(narrowings, tenantId);
};

export default useAttributeNarrowing;
